/**
 * /wtb/:breedSlug — Programmatic SEO. Aggregates open WTB posts for a
 * specific breed so search engines surface "wanted-to-buy {breed}" queries.
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import AppShell from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CompassIcon as Compass, GlobeHemisphereWestIcon as GlobeHemisphereWest } from "@phosphor-icons/react";

export default function WtbByBreed() {
  const { breedSlug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/seo/wtb/${breedSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [breedSlug]);

  const Shell = user ? AppShell : PublicShell;
  if (loading) return <Shell><div className="max-w-3xl mx-auto p-10 text-sm text-[#6B6B6B]">Loading…</div></Shell>;
  if (!data) return <Shell><div className="max-w-3xl mx-auto p-10"><p>Not found.</p></div></Shell>;

  const title = `Wanted-To-Buy ${data.breed} · Armstrong K9 Registry`;
  const description = `US breeders looking to import ${data.breed} foundation stock. Open requests with budgets and breeding-rights preferences. Foreign breeders pay $9 to reply.`;

  return (
    <Shell>
      <SeoHead
        title={title}
        description={description}
        url={`${webOrigin()}/wtb/${breedSlug}`}
        type="website"
      />
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="seo-wtb-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">Wanted-To-Buy · {data.breed}</p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Open requests for {data.breed} foundation stock
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Verified US breeders posting what they want to import. Foreign breeders pay $9 to send a single paid reply with photos and pricing.
        </p>

        {data.posts.length === 0 ? (
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-8 mt-8 text-center" data-testid="seo-wtb-empty">
            <Compass size={28} weight="duotone" className="mx-auto text-gold mb-2"/>
            <p className="font-semibold">No open {data.breed} requests right now.</p>
            <p className="text-sm text-[#6B6B6B] mt-1">
              <Link to="/marketplace/wtb" className="text-crimson underline">Browse the full WTB board</Link>{" "}
              or{" "}
              <Link to="/intl-breeders" className="text-crimson underline">browse the intl breeder directory</Link>.
            </p>
          </div>
        ) : (
          <div className="space-y-3 mt-8">
            {data.posts.map((p) => (
              <Link
                key={p.id}
                to={`/marketplace/wtb/${p.id}`}
                className="block bg-white border border-[#EAEAEA] rounded-xl p-5 hover:border-gold/55 hover:-translate-y-0.5 transition-all"
                data-testid={`seo-wtb-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-spectral text-lg tracking-tight leading-tight flex-1">{p.title}</h3>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] flex items-center gap-1">
                    <GlobeHemisphereWest size={12} weight="duotone"/>{p.poster_country}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#3F3F3F]">
                  {p.sex && p.sex !== "any" && <span>{p.sex}</span>}
                  {p.age_band && p.age_band !== "any" && <span>· {p.age_band}</span>}
                  {p.breeding_rights_required && (
                    <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">Breeding rights</span>
                  )}
                  {p.budget_max_usd ? <span className="ml-auto font-bold text-[#8A6A00]">Budget up to ${Number(p.budget_max_usd).toLocaleString()}</span> : null}
                </div>
                {p.notes && <p className="text-[12.5px] text-[#6B6B6B] mt-2 line-clamp-2">{p.notes}</p>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
